<template>
  <div class="d-flex flex-nowrap overflow-auto crisisTableContainer">
    <div class="p-1 w-100 crisis-report-panel">
      <div class="d-flex">
        <div class="d-flex">
          <h5>{{ $t('operational_security.crisis_form.master_data') }} -
            <span class="font-italic">{{ data.name }}-{{ data.reference_number }}</span>
          </h5>
        </div>
        <feather-icon @click="showFiltersFn" icon="FilterIcon" size="16" class="ml-1 cursor-pointer"/>
      </div>

      <div class="crisis-report-filters" v-if="showFilters">
        <h5>{{ $t('operational_security.crisis_form.filters_by_region_city') }}</h5>
        <div class="d-flex flex-wrap">
          <div v-for="(region, index) in selected" :key="region.region" class="crisis-report-filter-group">
            <b-form-checkbox
              :name="`option-filter-region${index}`"
              v-model="region.checked"
              :disabled="region.isDisabled"
              @change="onRegionFilterChange(region, $event)"
            >
              {{ region.region }}
            </b-form-checkbox>

            <div class="crisis-report-city-list" v-if="!region.checked">
              <b-form-checkbox
                v-for="city in region.cities"
                :key="city.key"
                :name="`option-filter-city${index}`"
                v-model="city.checked"
                :disabled="city.isDisabled"
              >
                {{ city.name  }}
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="crisis-report-filter-section">
          <h5>{{ $t("custom_forms.categories") }}</h5>
          <div class="metric-grid ml-1">
            <div
              v-for="(metric, index) in metricsFilters"
              :key="metric.name"
              class="metric-item"
            >
              <b-form-checkbox
                :name="`option-filter-metric${index}`"
                v-model="metric.checked"
                :disabled="metric.isDisabled"
              >
                {{ metric.name }}
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="crisis-report-filter-section">
          <b-form-checkbox
            name="Only rows with data"
            v-model="showOnlyRowsWithData"
          >
            {{  $t('operational_security.crisis_form.only_rows_with_data') }}
          </b-form-checkbox>
        </div>
      </div>

      <SkeletonLoading
        v-if="isMasterDataLoading"
        :count="1"
        :height="230"
        :fullWidth="true"
      />

      <div class="crisis-report-grid-shell">
        <crisis-report-grid
          v-if="!isMasterDataLoading && !dataNotExists"
          :source="gridRows"
          :columns="gridColumns"
          height="100%"
          :col-size="masterColumnSize"
          :header-lines="2"
          :header-row-height="masterHeaderRowHeight"
          :header-group-rows="2"
          :row-label-lines="2"
          :cell-lines="2"
        />

        <div class="w-100 h-100 d-flex justify-content-center align-items-center" v-if="dataNotExists">
        <p class="text-center">No existe información para esta crisis</p>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import SkeletonLoading from "@/components/SkeletonLoading.vue";
import {computed, getCurrentInstance, onMounted, ref, watch} from '@vue/composition-api';
import axios from "@/libs/axios";
import CrisisReportGrid from './CrisisReportGrid.vue';
import {
  hasAnyRegionOrCitySelection,
  makeColumnGroup,
  makeGridColumn,
  makeRowHeaderColumn,
  makeSafeProp,
  onRegionSelectionChange,
  ROW_LABEL_PROP
} from './composables/useCrisisReportGrid';
import {
  useCrisisFormUpdate
} from "@/views/operational_security/crisis_form/components/composables/useCrisisFormUpdateSocket";

const MASTER_COLUMN_SIZE = 75;
const MASTER_HEADER_ROW_HEIGHT = 56;

export default {
  components: {
    CrisisReportGrid,
    SkeletonLoading
  },
  props: {
    data: {
      type: Object,
    },
    slotId: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy;
    const { lastUpdate } = useCrisisFormUpdate(props.data.id);
    const masterData = ref([]);
    const grandTotals = ref([]);
    const selected = ref([]);
    const metricsFilters = ref([]);
    const showFilters = ref(false);
    const showOnlyRowsWithData = ref(false);
    const isMasterDataLoading = ref(false);
    const dataNotExists = ref(false);

    onMounted(() => {
      getMasterData();
    });

    watch(lastUpdate, () => getMasterData());

    const gridData = computed(() => {
      const columns = [makeRowHeaderColumn()];
      const rowsByMetric = {};
      const summaryTotals = {};
      const enabledMetrics = metricsFilters.value
        .filter(metric => metric.checked)
        .map(metric => metric.name);
      const hasAnySelection = hasAnyRegionOrCitySelection(selected.value);

      masterData.value.forEach(region => {
        const selectedRegion = selected.value.find(item => item.region === region.region);
        if (!selectedRegion) return;

        const filteredCities = getVisibleCities(region, selectedRegion, hasAnySelection);
        if (!filteredCities.length && !selectedRegion.checked) return;

        const metricMap = buildMetricMap(region.data, filteredCities, enabledMetrics);
        const metricNames = Object.keys(metricMap);
        if (!metricNames.length) return;

        const groupChildren = [];

        filteredCities.forEach(city => {
          const totalProp = makeSafeProp('master', region.region, city.key, 'total');
          const riskProp = makeSafeProp('master', region.region, city.key, 'risk');

          groupChildren.push(makeColumnGroup(city.label, [
            makeGridColumn({ prop: totalProp, name: 'Total', size: MASTER_COLUMN_SIZE }),
            makeGridColumn({
              prop: riskProp,
              name: vm.$t('operational_security.crisis_form.risk'),
              size: MASTER_COLUMN_SIZE
            })
          ]));

          metricNames.forEach(metricName => {
            const row = getMetricRow(rowsByMetric, metricName);
            row[totalProp] = metricMap[metricName].values[city.key]?.total ?? 0;
            row[riskProp] = metricMap[metricName].values[city.key]?.risk ?? 0;
          });
        });

        const totalProp = makeSafeProp('master', region.region, 'total');
        const riskProp = makeSafeProp('master', region.region, 'risk');
        groupChildren.push(makeColumnGroup('Total', [
          makeGridColumn({ prop: totalProp, name: 'Total', kind: 'total', size: MASTER_COLUMN_SIZE }),
          makeGridColumn({
            prop: riskProp,
            name: vm.$t('operational_security.crisis_form.risk'),
            kind: 'total',
            size: MASTER_COLUMN_SIZE
          })
        ]));

        metricNames.forEach(metricName => {
          const row = getMetricRow(rowsByMetric, metricName);
          row[totalProp] = metricMap[metricName].totals.total;
          row[riskProp] = metricMap[metricName].totals.risk;
          addSummaryTotals(summaryTotals, metricName, metricMap[metricName].totals);
        });
        const finalGroupChildren = selectedRegion.checked ? groupChildren.filter(group => group.name === "Total") : groupChildren;
        columns.push(makeColumnGroup(region.region, finalGroupChildren));
      });

      const rows = Object.values(rowsByMetric);
      const visibleRows = showOnlyRowsWithData.value ? filterRowsWithData(rows) : rows;

      if (visibleRows.length) {
        const summaryTotalProp = makeSafeProp('master', 'resumen', 'total');
        const summaryRiskProp = makeSafeProp('master', 'resumen', 'risk');
        columns.push(makeColumnGroup('Resumen', [
          makeGridColumn({ prop: summaryTotalProp, name: 'Total', kind: 'summary', size: MASTER_COLUMN_SIZE }),
          makeGridColumn({
            prop: summaryRiskProp,
            name: vm.$t('operational_security.crisis_form.risk'),
            kind: 'summary',
            size: MASTER_COLUMN_SIZE
          })
        ]));

        visibleRows.forEach(row => {
          const totals = summaryTotals[row[ROW_LABEL_PROP]] || { total: 0, risk: 0 };
          row[summaryTotalProp] = totals.total;
          row[summaryRiskProp] = totals.risk;
        });
      }

      return {
        columns,
        rows: visibleRows
      };
    });

    const gridColumns = computed(() => gridData.value.columns);
    const gridRows = computed(() => gridData.value.rows);

    function getVisibleCities(region, selectedRegion, hasAnySelection) {
      if (!hasAnySelection || selectedRegion.checked) {
        return region.cities.map(city => ({
          key: city.key,
          label: city.name
        }));
      }

      return selectedRegion.cities
        .filter(city => city.checked)
        .map(city => ({
          key: city.key,
          label: city.name
        }));
    }

    function buildMetricMap(regionData, cities, enabledMetrics) {
      const cityKeys = new Set(cities.map(city => city.key));
      const dataMap = {};

      regionData.forEach(item => {
        const metricEnabled =
          enabledMetrics.length === 0 ||
          enabledMetrics.some(metric => item.metric.includes(metric));

        if (!metricEnabled || !cityKeys.has(item.city)) return;

        if (!dataMap[item.metric]) {
          dataMap[item.metric] = {
            values: {},
            totals: { total: 0, risk: 0 }
          };
        }

        const total = item.total ?? 0;
        const risk = item.risk ?? item.at_risk ?? 0;
        dataMap[item.metric].values[item.city] = { total, risk };
        dataMap[item.metric].totals.total += total;
        dataMap[item.metric].totals.risk += risk;
      });

      return dataMap;
    }

    function getMetricRow(rowsByMetric, metricName) {
      if (!rowsByMetric[metricName]) {
        rowsByMetric[metricName] = {
          [ROW_LABEL_PROP]: metricName
        };
      }

      return rowsByMetric[metricName];
    }

    function addSummaryTotals(summaryTotals, metricName, totals) {
      if (!summaryTotals[metricName]) {
        summaryTotals[metricName] = { total: 0, risk: 0 };
      }

      summaryTotals[metricName].total += totals.total;
      summaryTotals[metricName].risk += totals.risk;
    }

    function filterRowsWithData(rows) {
      return rows.filter(row =>
        Object.keys(row).some(key =>
          key !== ROW_LABEL_PROP &&
          Number(row[key]) > 0
        )
      );
    }

    async function getMasterData() {
      try {
        isMasterDataLoading.value = true;
        dataNotExists.value = false;
        const {data} = await axios.get(`/v1/operational-security-crisis/${props.data.id}/base-master`);
        masterData.value = data.data.regions || [];
        grandTotals.value = data.data.grand_totals || [];
        selected.value = masterData.value.map(region => ({
          region: region.region,
          checked: false,
          isDisabled: false,
          cities: region.cities.map(city => ({
            key: city.key,
            name: city.name,
            checked: false,
            isDisabled: false
          }))
        }));
        setMetricsFilters();
      } catch (error) {
        dataNotExists.value = true;
      } finally {
        isMasterDataLoading.value = false;
      }
    }

    function showFiltersFn() {
      showFilters.value = !showFilters.value;
    }

    function onRegionFilterChange(region, checked) {
      onRegionSelectionChange(region, checked);
    }

    function setMetricsFilters() {
      const uniqueMetrics = [
        ...new Set(
          masterData.value.flatMap(item =>
            item.data.map(metricItem => metricItem.metric.split(' - ')[0])
          )
        )
      ];

      metricsFilters.value = uniqueMetrics.map(metric => ({
        name: metric,
        checked: false,
        isDisabled: false
      }));
    }

    return {
      gridColumns,
      gridRows,
      masterColumnSize: MASTER_COLUMN_SIZE,
      masterHeaderRowHeight: MASTER_HEADER_ROW_HEIGHT,
      isMasterDataLoading,
      dataNotExists,
      grandTotals,
      selected,
      showFilters,
      metricsFilters,
      showOnlyRowsWithData,
      showFiltersFn,
      onRegionFilterChange
    };
  }
};
</script>

<style scoped lang="scss">
.crisisTableContainer {
  height: 100%;
  min-height: 0;
  background-color: #fff;
}

.crisis-report-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.crisis-report-grid-shell {
  flex: 1 1 auto;
  min-height: 420px;
  height: calc(100vh - 240px);
}

.crisis-report-filters {
  margin: 12px 0 16px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.crisis-report-filter-group {
  margin: 0 18px 14px 0;
}

.crisis-report-city-list {
  margin: 8px 0 0 16px;
}

.crisis-report-filter-section {
  margin-top: 14px;
}

.metric-grid {
  max-width: 650px;
  max-height: 150px;
  display: flex;
  flex-wrap: wrap;
}

.metric-item {
  width: 100px;
}
</style>
